import React from "react";
import LegalPageShell from "./LegalPageShell";

const DEFAULT_BODY = `
<h2>1. Agreement</h2>
<p>These Terms of Service (“<strong>Terms</strong>”) form a binding agreement between you and Dream Home Engineers (“<strong>we</strong>”, “<strong>us</strong>”) governing your access to and use of <a href="https://plans.dreamhomeengineers.com">plans.dreamhomeengineers.com</a> (the “<strong>Site</strong>”) and any plans, services, or content purchased through the Site. By using the Site or purchasing a plan you agree to these Terms.</p>

<h2>2. Eligibility &amp; accounts</h2>
<ul>
  <li>You must be at least 18 years old and legally able to enter into a contract in your jurisdiction.</li>
  <li>You are responsible for the accuracy of information you provide and for keeping your account credentials secure.</li>
  <li>You must not share your account with others or use another person's account without permission.</li>
  <li>We may suspend or terminate accounts that violate these Terms or that we reasonably believe are being used fraudulently.</li>
</ul>

<h2>3. What you are buying — plan license</h2>
<p>When you purchase a house plan you receive a <strong>single-use license</strong> for a natural person or legal entity to construct <strong>one (1) dwelling</strong> from the plan on a single lot. Unless expressly upgraded to a multi-build or reseller license, you may not:</p>
<ul>
  <li>Build the plan more than once.</li>
  <li>Resell, sublicense, redistribute, or share the plan files.</li>
  <li>Publish the plan (in whole or in part) on any public medium or plan-sharing service.</li>
  <li>Use the plan as the basis for a derivative work sold or distributed to third parties.</li>
</ul>
<p>All copyright, moral rights, and design rights in the plan remain with Dream Home Engineers.</p>

<h2>4. Modifications &amp; custom work</h2>
<p>You may modify a purchased plan for your own single-build project. Modifications performed by our Custom Design studio are quoted separately and delivered on the timeline stated in the quote. Modifications performed by you or a third party remain your responsibility and are not covered by our warranty.</p>

<h2>5. Building-code compliance</h2>
<p>Our plans are drawn to universally recognised architectural standards. <strong>Local structural, electrical, plumbing, energy, seismic, and zoning-code compliance is the buyer's responsibility.</strong> You must engage a licensed local architect or structural engineer to review the plan against your local code before submitting it for permit. We are happy to adapt plans to local code as part of our Custom Design service.</p>

<h2>6. Payment &amp; taxes</h2>
<p>Prices displayed on the Site are exclusive of applicable taxes unless stated otherwise. All payments are processed by our third-party processors (Stripe / PayPal / Razorpay). By purchasing you authorise us and our processors to charge the payment method you provide. Currency conversions, foreign-exchange fees, and card-network fees are your responsibility.</p>

<h2>7. Delivery of files</h2>
<p>Plans are delivered digitally after successful payment. Standard delivery is instant download via your account's "My Plans" page. Custom-design deliverables follow the milestones in your engagement letter. Reasonable technical delays (payment provider webhook lag, file-generation queues) may result in a short delay.</p>

<h2>8. Refunds</h2>
<p>Please see our <a href="/refund">Refund &amp; Cancellation Policy</a> for full details on refund eligibility.</p>

<h2>9. Acceptable use</h2>
<p>You agree not to:</p>
<ul>
  <li>Reverse-engineer, decompile, or scrape the Site.</li>
  <li>Interfere with security features (watermarks, DRM, rate limits, authentication).</li>
  <li>Upload viruses, malware, or malicious content.</li>
  <li>Use the Site to violate any law or the intellectual-property rights of any party.</li>
  <li>Impersonate another person or entity.</li>
</ul>

<h2>10. User content &amp; enquiries</h2>
<p>By submitting an enquiry, review, or other content to the Site you grant us a worldwide, royalty-free, non-exclusive license to use, reproduce, and display that content in connection with operating the Site. You warrant that you own the content or have permission to submit it.</p>

<h2>11. Third-party services</h2>
<p>The Site integrates with third-party services (payments, analytics, email delivery, bot protection, cloud hosting). Your use of those services is governed by their own terms and privacy policies.</p>

<h2>12. Warranties &amp; disclaimers</h2>
<p>The Site and plans are provided <strong>“as is”</strong> and <strong>“as available”</strong>. We do not warrant that the Site will be uninterrupted, error-free, or that plans will meet a specific building requirement without modification. We disclaim implied warranties of merchantability, fitness for a particular purpose, and non-infringement to the maximum extent permitted by law.</p>

<h2>13. Limitation of liability</h2>
<p>To the maximum extent permitted by law, our aggregate liability arising out of or relating to these Terms, the Site, or any plan is limited to the total amount you paid to us in the 12 months preceding the event giving rise to the claim, or USD 500, whichever is greater. We are not liable for indirect, incidental, consequential, or punitive damages, including lost profits or construction cost overruns.</p>

<h2>14. Indemnification</h2>
<p>You agree to indemnify and hold Dream Home Engineers, its affiliates, officers, and employees harmless from any claim, damage, or expense arising from (a) your breach of these Terms, (b) your violation of any law, or (c) your misuse of a plan (including building without required permits or licensed review).</p>

<h2>15. Governing law &amp; disputes</h2>
<p>These Terms are governed by the laws of India (with respect to consumer-protection laws that apply mandatorily in your jurisdiction). Any dispute shall be brought exclusively in the courts of Hyderabad, Telangana, India, subject to any mandatory local-consumer forum in your jurisdiction.</p>

<h2>16. Changes to these Terms</h2>
<p>We may update these Terms from time to time. The current version is always available at <a href="/terms">/terms</a>. Material changes will be announced with at least 14 days' notice; your continued use of the Site after the effective date constitutes acceptance.</p>

<h2>17. Contact</h2>
<p>Questions about these Terms: <a href="mailto:nagesh@dreamhomeengineers.com">nagesh@dreamhomeengineers.com</a></p>
`;

const Terms = () => (
  <LegalPageShell
    slug="terms"
    title="Terms of Service"
    effectiveDate="21 July 2026"
    summary="Your agreement with Dream Home Engineers when you browse, purchase, or use plans from the Dream Home Plans marketplace."
    defaultBody={DEFAULT_BODY}
    contactEmail="nagesh@dreamhomeengineers.com"
  />
);

export default Terms;
